import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {motion, useScroll, useSpring, useTransform} from 'framer-motion'
import {useLinkResolver} from 'prismicio'
import {useEffect, useRef, useState} from 'react'
import {STRESSTIMONIALS_MOBILE_ANIMATION_STYLES} from 'src/common/constants/stresstimonialsAnimations'
import {Container} from 'src/common/Container'
import Drawer from 'src/common/Drawer'
import {getButtonsData} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {PlusButton} from 'src/common/PlusButton'
import {StresstimonialsDrawerContent} from 'src/common/StresstimonialsDrawerContent'
import type {Context} from 'src/common/types'

/**
 * Props for `StresstimonialsVideo`.
 */
export type StresstimonialsVideoProps = SliceComponentProps<Content.StresstimonialsVideoSlice>

/**
 * Component for "StresstimonialsVideo" Slices.
 */
const StresstimonialsVideo = ({
  slice,
  context: {pageUid, buttonsData},
  index: componentIndex,
}: StresstimonialsVideoProps & {context: Context}): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const domainCountry = useGetDomainCountry()
  const isVideoLeft = slice.primary.video_position === 'On the left'
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const {scrollYProgress} = useScroll({
    target: containerRef,
    offset: ['start 95%', 'start 50%'],
  })

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Intersection Observer for mobile CSS animation trigger
  useEffect(() => {
    if (!isMobile || !containerRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting)
        })
      },
      {
        threshold: 0.1,
        rootMargin: '0px',
      },
    )

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [isMobile])

  const animationDistance = isMobile ? 50 : 100
  const rawSectionX = useTransform(scrollYProgress, [0, 1], [isVideoLeft ? -animationDistance : animationDistance, 0])

  // Add spring animation for smooth experience
  const sectionX = useSpring(rawSectionX, {
    stiffness: 150,
    damping: 25,
    mass: 0.5,
  })

  const {primaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.cta_button,
  })

  const VideoWrapper = motion.div

  // Video autoplay while it is in ViewPort
  useEffect(() => {
    const video = videoRef.current

    if (!video) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const playPromise = video.play()

            if (playPromise !== undefined) {
              playPromise.catch(() => {
                // Autoplay was likely blocked; ignore to prevent unhandled promise rejection.
              })
            }
          } else {
            video.pause()
          }
        })
      },
      {
        threshold: 0,
        rootMargin: '400px 0px 0px 0px',
      },
    )

    observer.observe(video)

    return () => {
      observer.disconnect()
    }
  }, [isMobile])

  return (
    <>
      <style>{STRESSTIMONIALS_MOBILE_ANIMATION_STYLES}</style>
      <Container
        backgroundColor="Transparent"
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="!py-[30px] md:!py-[90px]"
      >
        <div ref={containerRef} className="mx-auto w-full max-w-[1280px] px-4 md:px-20">
          <VideoWrapper
            className={cn('flex gap-12 md:flex-row md:items-start md:gap-12', {
              'flex-col-reverse': isVideoLeft,
              'flex-col': !isVideoLeft,
              'md:flex-row': isVideoLeft,
              'md:flex-row-reverse': !isVideoLeft,
              'mobile-card-animated': isMobile,
              'mobile-card-visible': isMobile && isVisible,
            })}
            style={isMobile ? undefined : {x: sectionX}}
          >
            <div
              className="group flex w-full cursor-pointer flex-col gap-4 md:flex-[8]"
              onClick={(e) => {
                e.stopPropagation()

                trackMossAnalytics({
                  event: 'interaction',
                  interaction: {
                    element: `StresstimonialsVideo-${
                      isFilled.keyText(slice.primary.drawer_title) ? slice.primary.drawer_title : 'unknown'
                    }`,
                    elementText: 'video-card',
                    location: `stresstimonials-video-block`,
                    type: 'click',
                  },
                  page: {
                    domainCountry,
                    type: 'stresstimonials',
                  },
                })

                setIsDrawerOpen(true)
              }}
            >
              {isFilled.keyText(slice.primary.eyebrow) && (
                <div className="flex flex-col gap-2.5">
                  <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
                  <p className="text-[11px] font-semibold uppercase tracking-[0.66px] text-moss-grey-400">
                    {slice.primary.eyebrow}
                  </p>
                  <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
                </div>
              )}
              {isFilled.linkToMedia(slice.primary.video) && (
                <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-moss-grey-100 ring-0 transition-all duration-300 group-hover:ring-2 group-hover:ring-green-neon">
                  <video ref={videoRef} loop muted playsInline preload="auto" className="size-full object-cover">
                    <source src={slice.primary.video.url} type="video/mp4" />
                  </video>
                  <PlusButton className="absolute bottom-3 right-3" />
                </div>
              )}
              {isFilled.richText(slice.primary.bottom_comment) && (
                <div className="flex flex-col gap-4">
                  <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
                  <div>
                    <PrismicRichText
                      components={{
                        paragraph: ({children}) => (
                          <p className="text-18 font-semibold uppercase leading-normal tracking-[1.08px] text-moss-black">
                            {children}
                          </p>
                        ),
                      }}
                      field={slice.primary.bottom_comment}
                    />
                  </div>
                  <div className="h-px w-full border-b border-dashed border-moss-grey-400" />
                </div>
              )}
            </div>

            {isFilled.image(slice.primary.image) && (
              <div className="w-[146px] md:flex-[2]">
                <PrismicNextImage field={slice.primary.image} className="h-auto w-full" />
              </div>
            )}
          </VideoWrapper>
        </div>

        <Drawer isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen}>
          <StresstimonialsDrawerContent
            video_source={slice.primary.video_source}
            fallback_image={slice.primary.drawer_video_fallback_image}
            drawer_title={slice.primary.drawer_title}
            drawer_bio_title={slice.primary.drawer_bio_title}
            bio_info={slice.primary.bio_info}
            free_text={slice.primary.free_text}
            drawer_image={slice.primary.drawer_image}
            free_text_2={slice.primary.free_text_2}
            cta_eyebrow={slice.primary.cta_eyebrow}
            cta_title={slice.primary.cta_title}
            cta_description={slice.primary.cta_description}
            primaryButtonData={primaryButtonData}
            linkResolver={linkResolver}
            componentIndex={componentIndex}
            pageUid={pageUid}
            componentName="stresstimonials-video"
          />
        </Drawer>
      </Container>
    </>
  )
}

export default StresstimonialsVideo
